import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui';
import { ThumbsDown } from '@/components/ui/icons/Icons';
import { useI18n } from '@/context/I18nProvider';
import { cn } from '@/lib/utils';
import { MessageFeedback } from '@/services/types/Lead.types';
import { Dispatch, forwardRef, SetStateAction } from 'react';
import { MessageType } from './ChatMessage';

type ChatMessageFeedbackButtonProps = {
  type: MessageType;
  leadId?: string;
  messageId?: string;
  feedback?: MessageFeedback;
  showFeedbackInput: boolean;
  setShowFeedbackInput: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
  sentByAI?: boolean;
  feedbackAlreadySentOverride?: boolean;
};

const ChatMessageFeedbackButton = forwardRef<HTMLButtonElement, ChatMessageFeedbackButtonProps>(
  (
    {
      type,
      leadId,
      messageId,
      feedback,
      showFeedbackInput,
      setShowFeedbackInput,
      isLoading,
      sentByAI = true,
      feedbackAlreadySentOverride,
    },
    ref
  ) => {
    const { t } = useI18n();
    const isOutbound = type === 'outbound';
    const feedbackAlreadySent = feedbackAlreadySentOverride ?? !!feedback?.FeedbackContent;
    const isButtonDisabled = isLoading;

    if (!isOutbound || !sentByAI || !leadId || !messageId) {
      return null;
    }

    return (
      <TooltipProvider>
        <Tooltip
          open={showFeedbackInput || isButtonDisabled ? false : undefined}
          delayDuration={50}
        >
          <TooltipTrigger asChild>
            <Button
              ref={ref}
              variant="ghost"
              type="button"
              className={cn(
                'size-4 bg-white text-text-primary h-auto p-0 hover:bg-white rounded-none focus-visible:ring-0 focus-visible:text-primary disabled:bg-white',
                feedbackAlreadySent
                  ? 'text-red-500 cursor-not-allowed hover:text-red-500'
                  : 'hover:text-primary transition-colors',
                showFeedbackInput && 'text-primary cursor-default'
              )}
              onClick={() => {
                if (!feedbackAlreadySent) setShowFeedbackInput(!showFeedbackInput);
              }}
              disabled={isButtonDisabled}
            >
              <ThumbsDown />
            </Button>
          </TooltipTrigger>
          <TooltipContent
            className={cn(
              'max-w-56 bg-white text-text-primary font-normal px-2 py-1 border-[#F3F4F6] border shadow',
              feedbackAlreadySent && 'min-w-44 min-h-12 flex flex-col items-start gap-1 pb-2'
            )}
          >
            {feedbackAlreadySent ? (
              <>
                <span className="text-text-primary font-semibold">
                  {t('feedback.yourFeedback')}
                </span>
                <div className="text-xs text-text-primary">{feedback?.FeedbackContent}</div>
              </>
            ) : (
              t('feedback.giveFeedback')
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

ChatMessageFeedbackButton.displayName = 'ChatMessageFeedbackButton';

export { ChatMessageFeedbackButton };
